import type { MaterialCategory } from '@/lib/types'

/**
 * Display-only grouping of categories (`MaterialCategory.groupName`) — a port of the app's
 * `CategoryGroups`. Nothing links or calculates by the group; it only arranges lists. Names are
 * compared ignoring case and repeated spaces; the first spelling met is shown.
 */

/** Trims and collapses inner whitespace; this is what gets stored. */
export function normalizeGroupName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/** Comparison key of a group name ('' = no group). */
export function groupKey(raw: string): string {
  return normalizeGroupName(raw).toLowerCase()
}

export type CategorySection = { name: string | null; categories: MaterialCategory[] }

/**
 * Splits `categories` (already in display order) into sections: named groups alphabetically, then
 * the ungrouped ones. Each section keeps the input order.
 */
export function categorySections(categories: readonly MaterialCategory[]): CategorySection[] {
  const byKey = new Map<string, MaterialCategory[]>()
  const spelling = new Map<string, string>()
  for (const category of categories) {
    const key = groupKey(category.groupName)
    const list = byKey.get(key)
    if (list) list.push(category)
    else byKey.set(key, [category])
    if (key !== '' && !spelling.has(key)) spelling.set(key, normalizeGroupName(category.groupName))
  }
  const named = [...byKey.keys()]
    .filter((key) => key !== '')
    // UTF-16 code units, like Kotlin's `sorted()` in the app, so both list the groups identically.
    .sort((left, right) => (left < right ? -1 : left > right ? 1 : 0))
    .map((key) => ({ name: spelling.get(key) ?? key, categories: byKey.get(key) ?? [] }))
  const rest = byKey.get('')
  return rest ? [...named, { name: null, categories: rest }] : named
}

/** The distinct group names in use (first spelling met), alphabetically. */
export function categoryGroupNames(categories: readonly MaterialCategory[]): string[] {
  return categorySections(categories).flatMap((section) => (section.name === null ? [] : [section.name]))
}

/** «Группа — Категория», or just the name when the category has no group. */
export function categoryLabel(category: MaterialCategory): string {
  const group = normalizeGroupName(category.groupName)
  return group ? `${group} — ${category.name}` : category.name
}